import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, AlertTriangle, TrendingUp, RefreshCw, Edit2, Save, X, Truck, BarChart3 } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import StockLevelCard from './components/StockLevelCard';
import LocationInventoryPanel from './components/LocationInventoryPanel';
import LowStockAlertsPanel from './components/LowStockAlertsPanel';
import ReplenishmentPanel from './components/ReplenishmentPanel';

export default function CentralizedBarrelInventoryManagementSystem() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  
  // Inventory data
  const [barrelOrders, setBarrelOrders] = useState([]);
  const [barrelDeposits, setBarrelDeposits] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [inventoryByLocation, setInventoryByLocation] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  
  // Stock thresholds configuration
  const [thresholds, setThresholds] = useState({
    noir_270l: { low: 20, critical: 10, reorder: 50 },
    bleu_220l: { low: 15, critical: 8, reorder: 40 }
  });
  
  const [editingThresholds, setEditingThresholds] = useState(false);
  const [tempThresholds, setTempThresholds] = useState(thresholds);

  useEffect(() => {
    loadInventoryData();
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(() => {
      loadInventoryData(true);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadInventoryData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError('');
      
      const [ordersData, depositsData, statsData] = await Promise.all([
        barrelService?.getBarrelOrders(),
        barrelService?.getBarrelDeposits(),
        barrelService?.getBarrelStatistics()
      ]);
      
      setBarrelOrders(ordersData);
      setBarrelDeposits(depositsData);
      setStatistics(statsData);
      
      // Calculate inventory by location
      const locationInventory = calculateLocationInventory(ordersData, depositsData);
      setInventoryByLocation(locationInventory);
      
      // Calculate low stock alerts
      const alerts = calculateLowStockAlerts(locationInventory, thresholds);
      setLowStockAlerts(alerts);
    } catch (err) {
      console.error('Error loading inventory data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const calculateLocationInventory = (orders, deposits) => {
    // Group by location (city)
    const locationMap = new Map();
    
    // Count delivered barrels by location
    orders?.forEach(order => {
      if (order?.status === 'delivered' || order?.status === 'filled') {
        const location = order?.customer_city || 'Non spécifié';
        if (!locationMap?.has(location)) {
          locationMap?.set(location, {
            location,
            noir_270l: { total: 0, available: 0, reserved: 0 },
            bleu_220l: { total: 0, available: 0, reserved: 0 }
          });
        }
        
        const loc = locationMap?.get(location);
        const type = order?.barrel_type;
        loc[type].total += order?.quantity || 0;
        
        // Check if barrel is reserved for collection
        const hasDeposit = deposits?.some(d => 
          d?.barrel_order_id === order?.id && 
          (d?.collection_status === 'pending' || d?.collection_status === 'scheduled')
        );
        
        if (hasDeposit) {
          loc[type].reserved += order?.quantity || 0;
        } else {
          loc[type].available += order?.quantity || 0;
        }
      }
    });
    
    return Array.from(locationMap?.values());
  };

  const calculateLowStockAlerts = (locationInventory, thresholds) => {
    const alerts = [];
    
    locationInventory?.forEach(loc => {
      ['noir_270l', 'bleu_220l']?.forEach(type => {
        const stock = loc?.[type];
        const threshold = thresholds?.[type];
        
        if (stock?.available <= threshold?.critical) {
          alerts?.push({
            location: loc?.location,
            barrelType: type,
            currentStock: stock?.available,
            threshold: threshold?.critical,
            severity: 'critical',
            message: `Stock critique à ${loc?.location} pour ${getBarrelLabel(type)}`
          });
        } else if (stock?.available <= threshold?.low) {
          alerts?.push({
            location: loc?.location,
            barrelType: type,
            currentStock: stock?.available,
            threshold: threshold?.low,
            severity: 'warning',
            message: `Stock bas à ${loc?.location} pour ${getBarrelLabel(type)}`
          });
        }
      });
    });
    
    return alerts;
  };

  const getBarrelLabel = (type) => {
    return type === 'noir_270l' ? 'Barrique Noire 270L' : 'Barrique Bleue 220L';
  };

  const getBarrelPrice = (type) => {
    return type === 'noir_270l' ? 35 : 40;
  };

  const calculateTotalStock = (type) => {
    return inventoryByLocation?.reduce((sum, loc) => sum + loc?.[type]?.total, 0);
  };

  const calculateAvailableStock = (type) => {
    return inventoryByLocation?.reduce((sum, loc) => sum + loc?.[type]?.available, 0);
  };

  const calculateReservedStock = (type) => {
    return inventoryByLocation?.reduce((sum, loc) => sum + loc?.[type]?.reserved, 0);
  };

  const getStockStatus = (available, type) => {
    const threshold = thresholds?.[type];
    if (available <= threshold?.critical) return 'critical';
    if (available <= threshold?.low) return 'warning';
    return 'good';
  };

  const handleSaveThresholds = () => {
    setThresholds(tempThresholds);
    setEditingThresholds(false);
    // Recalculate alerts with new thresholds
    const alerts = calculateLowStockAlerts(inventoryByLocation, tempThresholds);
    setLowStockAlerts(alerts);
  };

  const handleCancelThresholds = () => {
    setTempThresholds(thresholds);
    setEditingThresholds(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement de l'inventaire...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion Inventaire Barriques</h1>
            <p className="text-sm text-gray-600 mt-1">Suivi temps réel des stocks et emplacements</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/barrel-stock-analytics-dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <BarChart3 size={18} />
              <span className="hidden sm:inline">Analytics</span>
            </button>
            <button
              onClick={() => loadInventoryData()}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Stock Level Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <StockLevelCard
            barrelType="noir_270l"
            label="Barrique Noire 270L"
            price={35}
            totalStock={calculateTotalStock('noir_270l')}
            availableStock={calculateAvailableStock('noir_270l')}
            reservedStock={calculateReservedStock('noir_270l')}
            status={getStockStatus(calculateAvailableStock('noir_270l'), 'noir_270l')}
            threshold={thresholds?.noir_270l}
          />
          <StockLevelCard
            barrelType="bleu_220l"
            label="Barrique Bleue 220L"
            price={40}
            totalStock={calculateTotalStock('bleu_220l')}
            availableStock={calculateAvailableStock('bleu_220l')}
            reservedStock={calculateReservedStock('bleu_220l')}
            status={getStockStatus(calculateAvailableStock('bleu_220l'), 'bleu_220l')}
            threshold={thresholds?.bleu_220l}
          />
        </div>

        {/* Threshold Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Configuration des Seuils</h2>
            {!editingThresholds ? (
              <button
                onClick={() => setEditingThresholds(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Edit2 size={16} />
                Modifier
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveThresholds}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Save size={16} />
                  Enregistrer
                </button>
                <button
                  onClick={handleCancelThresholds}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <X size={16} />
                  Annuler
                </button>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Noir 270L Thresholds */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Barrique Noire 270L</h3>
              <div className="space-y-2">
                <div>
                  <label className="text-sm text-gray-600">Seuil Stock Bas</label>
                  <input
                    type="number"
                    value={editingThresholds ? tempThresholds?.noir_270l?.low : thresholds?.noir_270l?.low}
                    onChange={(e) => setTempThresholds({
                      ...tempThresholds,
                      noir_270l: { ...tempThresholds?.noir_270l, low: parseInt(e?.target?.value) }
                    })}
                    disabled={!editingThresholds}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Seuil Critique</label>
                  <input
                    type="number"
                    value={editingThresholds ? tempThresholds?.noir_270l?.critical : thresholds?.noir_270l?.critical}
                    onChange={(e) => setTempThresholds({
                      ...tempThresholds,
                      noir_270l: { ...tempThresholds?.noir_270l, critical: parseInt(e?.target?.value) }
                    })}
                    disabled={!editingThresholds}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Quantité Réapprovisionnement</label>
                  <input
                    type="number"
                    value={editingThresholds ? tempThresholds?.noir_270l?.reorder : thresholds?.noir_270l?.reorder}
                    onChange={(e) => setTempThresholds({
                      ...tempThresholds,
                      noir_270l: { ...tempThresholds?.noir_270l, reorder: parseInt(e?.target?.value) }
                    })}
                    disabled={!editingThresholds}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* Bleu 220L Thresholds */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Barrique Bleue 220L</h3>
              <div className="space-y-2">
                <div>
                  <label className="text-sm text-gray-600">Seuil Stock Bas</label>
                  <input
                    type="number"
                    value={editingThresholds ? tempThresholds?.bleu_220l?.low : thresholds?.bleu_220l?.low}
                    onChange={(e) => setTempThresholds({
                      ...tempThresholds,
                      bleu_220l: { ...tempThresholds?.bleu_220l, low: parseInt(e?.target?.value) }
                    })}
                    disabled={!editingThresholds}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Seuil Critique</label>
                  <input
                    type="number"
                    value={editingThresholds ? tempThresholds?.bleu_220l?.critical : thresholds?.bleu_220l?.critical}
                    onChange={(e) => setTempThresholds({
                      ...tempThresholds,
                      bleu_220l: { ...tempThresholds?.bleu_220l, critical: parseInt(e?.target?.value) }
                    })}
                    disabled={!editingThresholds}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">Quantité Réapprovisionnement</label>
                  <input
                    type="number"
                    value={editingThresholds ? tempThresholds?.bleu_220l?.reorder : thresholds?.bleu_220l?.reorder}
                    onChange={(e) => setTempThresholds({
                      ...tempThresholds,
                      bleu_220l: { ...tempThresholds?.bleu_220l, reorder: parseInt(e?.target?.value) }
                    })}
                    disabled={!editingThresholds}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Low Stock Alerts */}
        {lowStockAlerts?.length > 0 && (
          <LowStockAlertsPanel alerts={lowStockAlerts} onRefresh={loadInventoryData} />
        )}

        {/* Location Inventory Panel */}
        <LocationInventoryPanel 
          inventoryByLocation={inventoryByLocation}
          thresholds={thresholds}
        />

        {/* Replenishment Panel */}
        <ReplenishmentPanel 
          lowStockAlerts={lowStockAlerts}
          thresholds={thresholds}
          onReplenishmentCreated={loadInventoryData}
        />

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Package size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Commandes Totales</p>
                <p className="text-xl font-bold text-gray-900">{statistics?.totalOrders || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Revenu Total</p>
                <p className="text-xl font-bold text-gray-900">{statistics?.totalRevenue?.toFixed(2) || 0} €</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Truck size={20} className="text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Collectes Planifiées</p>
                <p className="text-xl font-bold text-gray-900">{statistics?.scheduledCollections || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Alertes Actives</p>
                <p className="text-xl font-bold text-gray-900">{lowStockAlerts?.length || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}